import AdiaLive5Error from "./adiaLive5Error";
import LiveSwitchWebRTCHandler from "./liveSwitchWebRTCHandler";

export default class AdiaLive5WebRTCLayer {
	static getBrowserInfo(){
		const parser = require('ua-parser-js');
		const browserInfo = new parser();

		const browser = browserInfo.getBrowser();
		const os = browserInfo.getOS();

		const browserVersionParts = browser.version.split('.');

		let browserVersion = '';
		if(browserVersionParts.length > 1){
			browserVersion = browserVersionParts[0] + '.' + browserVersionParts[1];
		}
		else{
			browserVersion = browserVersionParts[0] + '.0';
		}

		return {browserName: browser.name, browserVersion: browserVersion, os: os.name};
	}

	static isAllowedBrowser(){
		const browserWhitelist = ['Chrome','Firefox','Opera','Edge','Safari','Mobile Safari','Samsung Browser'];
		const browserBlacklist = ['IE'];

		const browserMinVersions = {
			'Chrome': 66,
			'Firefox': 60,
			'Opera': 53,
			'Edge': 70,
			'Safari': 12.4,
			'Mobile Safari': 12.4,
			'Samsung Browser': 9.2
		}

		const browserInfo = AdiaLive5WebRTCLayer.getBrowserInfo();

		if(browserBlacklist.indexOf(browserInfo.browserName) > -1){
			return 'no';
		}

		if(browserWhitelist.indexOf(browserInfo.browserName) > -1) {
			// Compatible browser, check version
			if (isFinite(browserInfo.browserVersion)) {
				if (Number(browserInfo.browserVersion) >= browserMinVersions[browserInfo.browserName]) {
					return 'yes';
				}
				else{
					return 'no';
				}
			}
		}

		return 'unknown';
	}

	static isNonSafariOnIos(){
		const browserInfo = AdiaLive5WebRTCLayer.getBrowserInfo();

		const iPad = (navigator.userAgent.match(/(iPad)/) /* iOS pre 13 */ || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) /* iPad OS 13 */);

		// Reject all non-safari browsers on iOS
		if((iPad || browserInfo.os === 'iOS') && !(browserInfo.browserName === 'Safari' || browserInfo.browserName === 'Mobile Safari')){
			return true;
		}
		else{
			return false;
		}
	}

	static getCapabilities(){
		const hasWebRtc = (typeof RTCPeerConnection !== 'undefined');
		const hasOrtc = (typeof RTCIceTransport !== 'undefined' && typeof RTCDataChannel === 'undefined');
		let hasDisplayMedia = (window.navigator) ? !!(navigator.getDisplayMedia || (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia)) : false;

		if (navigator.userAgent.match(/Android/i)
			|| navigator.userAgent.match(/webOS/i)
			|| navigator.userAgent.match(/iPhone/i)
			|| navigator.userAgent.match(/iPad/i)
			|| navigator.userAgent.match(/iPod/i)
			|| navigator.userAgent.match(/BlackBerry/i)
			|| navigator.userAgent.match(/Windows Phone/i)){
			hasDisplayMedia = false;
		}

		const canRecordMedia = (!!window.Promise && !!window.indexedDB && !!window.MediaRecorder);

		return {
			hasOrtc: hasOrtc,
			hasWebRtc: hasWebRtc,
			hasNative: (hasOrtc || hasWebRtc),
			hasDisplayMedia: hasDisplayMedia,
			canRecordMedia: canRecordMedia
		}
	}

	static getBrowserCompatibility(){
		const browserInfo = AdiaLive5WebRTCLayer.getBrowserInfo();
		const isAllowedBrowser = AdiaLive5WebRTCLayer.isAllowedBrowser();

		let isBrowserCompatible = true;

		// Reject all non-safari browsers on iOS & incompatible browsers
		if(AdiaLive5WebRTCLayer.isNonSafariOnIos() || isAllowedBrowser === 'no'){
			isBrowserCompatible = false;
		}
		else if(isAllowedBrowser === 'unknown'){
			// Check unknown browsers for WebRTC support
			const browserCapabilities = AdiaLive5WebRTCLayer.getCapabilities();
			if(!browserCapabilities.hasWebRtc){
				isBrowserCompatible = false;
			}
		}

		browserInfo.compatible = isBrowserCompatible;

		return browserInfo;
	}

    constructor(options){
        this.options = options;

        this.camSettings = (options && options.hasOwnProperty('camSettings')) ? options.camSettings : null;

        this.maxVideoBitrate = (options && options.hasOwnProperty('maxVideoBitrate')) ? options.maxVideoBitrate : 0;
        this.maxAudioBitrate = (options && options.hasOwnProperty('maxAudioBitrate')) ? options.maxAudioBitrate : 0;

        this.appOnError = options.onError;
        this.appOnNotification = options.onNotification;
        this.appOnUserJoined = options.onUserJoined;
        this.appOnUserLeft = options.onUserLeft;
        this.appOnStatsReceived = options.onStatsReceived;
        this.appOnAudioLevel = options.onAudioLevel;
        this.appOnSpeakerDetected = options.onSpeakerDetected;
    }

    init(){
        const _this = this;

        if(this.options.mode !== 'liveSwitchSfu' && this.options.mode !== 'liveSwitchPeer' && this.options.mode !== 'adiaP2P'){
            return Promise.reject(new AdiaLive5Error(41));
        }

		const browserCompatibility = AdiaLive5WebRTCLayer.getBrowserCompatibility();
        if(!browserCompatibility.compatible){
			return Promise.reject(new AdiaLive5Error(51, null, browserCompatibility));
		}

        return new Promise(function(resolve){
            const webRTCHandlerOptions = {
                webRTCLayer: _this,
                //url: options.url,
                camSettings: _this.camSettings,
                communicationMode: _this.options.communicationMode,
				maxVideoBitrate: _this.maxVideoBitrate
            };

            if(_this.options.mode === 'liveSwitchSfu'){
                webRTCHandlerOptions.useSimulcast = !!_this.options.useSimulcast;
                webRTCHandlerOptions.adaptiveQuality = (_this.options.adaptiveQuality === false) ? false : true;
            }
            else{
                webRTCHandlerOptions.useSimulcast = false;
                webRTCHandlerOptions.adaptiveQuality = false;
            }

			webRTCHandlerOptions.connectionMode = _this.options.mode;
            webRTCHandlerOptions.enableLogging = !!_this.options.enableLogging;
            webRTCHandlerOptions.enableWebRtcLogging = !!_this.options.enableWebRtcLogging;

            _this.webRTCHandler = new LiveSwitchWebRTCHandler(webRTCHandlerOptions);

            const browserCapabilities = AdiaLive5WebRTCLayer.getCapabilities();
            resolve(browserCapabilities);
        })
    }

    getDeviceLists(communicationMode){
        return this.webRTCHandler.getDeviceLists(communicationMode);
    }

    setDevice(deviceType, deviceName){
        return this.webRTCHandler.setDevice(deviceType, deviceName);
    }

    setResolution(resolution){
        return this.webRTCHandler.setResolution(resolution);
    }

    setCommunicationMode(communicationMode){
        return this.webRTCHandler.setCommunicationMode(communicationMode);
    }

    getCurrentSettings(){
        return this.webRTCHandler.getCurrentSettings();
    }

    applySettings(settings){
        return this.webRTCHandler.applySettings(settings);
    }

    getSettingsComponents() {
        return this.webRTCHandler.getSettingsComponents();
    }

    getCamSettings(){
        return this.webRTCHandler.camSettings;
    }

    muteAudio(shouldMute){
        this.webRTCHandler.muteAudio(shouldMute);

    }

    muteVideo(shouldMute){
        this.webRTCHandler.muteVideo(shouldMute);
    }

    muteRemoteAudio(shouldMute, userId){
        this.webRTCHandler.muteRemoteAudio(shouldMute, userId);

    }

    muteRemoteVideo(shouldMute, userId){
        this.webRTCHandler.muteRemoteVideo(shouldMute, userId);
    }

    isDeviceMuted(deviceType){
        return this.webRTCHandler.isDeviceMuted(deviceType);
    }

    startLocalMedia(){
        return this.webRTCHandler.startLocalMedia();
    }

    stopLocalMedia(){
        return this.webRTCHandler.stopLocalMedia();
    }

    join(options, meetingInfo){
        return this.webRTCHandler.join(options, meetingInfo);
    }

    leave(){
        return this.webRTCHandler.leave();
    }

    startSettingsMode(previewModeSettings){
        return this.webRTCHandler.startSettingsMode(previewModeSettings);
    }

    stopSettingsMode(){
        return this.webRTCHandler.stopSettingsMode();
    }

    refreshPreviewDevices(){
        return this.webRTCHandler.refreshPreviewDevices();
    }

    startScreenshare(){
        return this.webRTCHandler.startScreenshare();
    }

    stopScreenshare(){
        return this.webRTCHandler.stopScreenshare();
    }

    forceQuit(){
        this.webRTCHandler.forceQuit();
    }

    startRecording(){
        return this.webRTCHandler.startRecording();
    }

    stopRecording(){
        this.webRTCHandler.stopRecording();
    }

    getRecordingList(){
        return this.webRTCHandler.getRecordingList();
    }

    downloadRecording(meetingId){
        return this.webRTCHandler.downloadRecording(meetingId);
    }

	getTimeline(meetingId){
		return this.webRTCHandler.getTimeline(meetingId);
	}

	deleteRecording(meetingId){
		return this.webRTCHandler.deleteRecording(meetingId);
	}

    updateMeetingInfo(meetingInfo){
        return this.webRTCHandler.updateMeetingInfo(meetingInfo);
    }

	setRemoteQuality(quality, userId){
		return this.webRTCHandler.setRemoteQuality(quality, userId);
	}

	// Events
    onError(error){
        this.appOnError(error);
    }

    onNotification(notification){
        this.appOnNotification(notification);
    }

    onUserJoined(userView){
        this.appOnUserJoined(userView);
    }

    onUserLeft(userView){
        this.appOnUserLeft(userView);
    }

    onStatsReceived(userStats){
        this.appOnStatsReceived(userStats);
    }

    onAudioLevel(userAudioLevel){
        this.appOnAudioLevel(userAudioLevel);
    }

    onSpeakerDetected(userId){
        this.appOnSpeakerDetected(userId);
    }
}
